import { AfterViewInit, Component, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { CopyButtonComponent } from './copy-button/copy-button.component';

import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { KorviuInputComponent } from '../../../../shared/components/korviu-input/korviu-input.component';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { from, ReplaySubject, takeUntil, tap } from 'rxjs';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { KorviuButtonComponent } from '../../../../shared/components/korviu-button/korviu-button.component';
import { CustomMessageService } from 'core/services/custom-message.service';
import { HideTooltipOnScrollDirective } from 'shared/directives';

@Component({
  selector: 'app-validate-us-address-modal',
  standalone: true,
  imports: [
    CopyButtonComponent,
    NzButtonModule,
    NzIconModule,
    NzTooltipModule,
    HideTooltipOnScrollDirective,
    TranslateModule,
    NzModalModule,
    KorviuInputComponent,
    KorviuButtonComponent
  ],
  templateUrl: './validate-us-address-modal.component.html',
  styleUrl: './validate-us-address-modal.component.scss'
})
export class ValidateUsAddressModalComponent implements OnDestroy, AfterViewInit {
  @ViewChild('addresstext') addresstext: KorviuInputComponent;

  public addressControl: FormControl;
  public placeControl: FormControl;
  public okeeyCashFormatForm: FormGroup;
  public okeeyCashFormatFormIsVisible: boolean;
  // @ts-ignore
  public selectedPlace: google.maps.places.PlaceResult;

  public get isValidateAddressDisabled(): boolean {
    return !this.selectedPlace;
  }

  public get street(): AbstractControl {
    return this.okeeyCashFormatForm.get('street');
  }

  public get apt(): AbstractControl {
    return this.okeeyCashFormatForm.get('apt');
  }

  public get zip(): AbstractControl {
    return this.okeeyCashFormatForm.get('zip');
  }

  public get city(): AbstractControl {
    return this.okeeyCashFormatForm.get('city');
  }

  private readonly _onDestroy$: ReplaySubject<void>;

  constructor(
    private ngZone: NgZone,
    private _messageService: CustomMessageService,
    private _translateService: TranslateService
  ) {
    this.addressControl = new FormControl('', [Validators.required]);
    this.placeControl = new FormControl('', [Validators.required]);
    this.okeeyCashFormatForm = new FormGroup<any>({
      street: new FormControl(''),
      apt: new FormControl(''),
      zip: new FormControl(''),
      city: new FormControl('')
    });
    this.okeeyCashFormatFormIsVisible = false;
    this._onDestroy$ = new ReplaySubject(1);
  }

  public ngAfterViewInit(): void {
    this.getPlaceAutocomplete();
  }

  private getPlaceAutocomplete() {
    // @ts-ignore
    const autocomplete = new google.maps.places.Autocomplete(this.addresstext.input.nativeElement, {
      componentRestrictions: { country: 'US' },
      types: ['address']
    });
    // @ts-ignore
    google.maps.event.addListener(autocomplete, 'place_changed', () => {
      this.ngZone.run(() => {
        this.selectedPlace = autocomplete.getPlace();
        this.placeControl.patchValue(this.selectedPlace);
      });
    });
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  public copyText(text: string, entityLocalizableName: string): void {
    from(navigator.clipboard.writeText(text))
      .pipe(
        tap(() => {
          this._messageService.info(
            this._translateService.instant(entityLocalizableName) + this._translateService.instant('okeeyCash.copied')
          );
        }),
        takeUntil(this._onDestroy$)
      )
      .subscribe();
  }

  public onValidateAddressClick(): void {
    this.formatPlaceResult();
    this.okeeyCashFormatFormIsVisible = true;
  }

  public formatPlaceResult(): void {
    const place = this.selectedPlace;
    let address = {
      streetAddress: '',
      aptSuite: '',
      zipCode: '',
      city: ''
    };

    for (let component of place.address_components) {
      const componentType = component.types[0];

      switch (componentType) {
        case 'street_number':
          address.streetAddress = `${component.long_name} ${address.streetAddress}`;
          break;
        case 'route':
          address.streetAddress += component.short_name;
          break;
        case 'subpremise':
          address.aptSuite = component.long_name;
          break;
        case 'postal_code':
          address.zipCode = component.long_name;
          break;
        case 'locality':
          address.city = component.long_name;
          break;
      }
    }

    this.street.patchValue(address.streetAddress);
    this.apt.patchValue(address.aptSuite);
    this.zip.patchValue(address.zipCode);
    this.city.patchValue(address.city);
  }
}
